import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { LoadingBar } from './loadingBar';
import NoteForm from './noteForm';
import { deleteModal } from 'util/modal';
import SectionHeader from './shell/SectionHeader';
import { bucketFrame, ui, button } from 'util/ui';
import { PlusIcon } from './shell/Icons';

/**
 * Notes bucket. UI refresh styling (5 Nov 2026): a light list on the left (selected note white with a
 * bar in the bucket colour, not solid green), the note on the right, and a clear empty state.
 * On a phone the list sits above the note. Behaviour unchanged.
 */

type Props = {
  notes: INote[];
  addNote(): void;
  updateNote(note: INote): void;
  deleteNote(id: string): void;
  loading: boolean;
};

export const Notes: React.FC<Props> = ({ notes, addNote, updateNote, deleteNote, loading }) => {
  const [selectedNote, setSelectedNote] = useState<INote | undefined>(notes.length > 0 ? notes[0] : undefined);
  const [pendingChanges, setPendingChanges] = useState(false);

  const selectNote = async (note: INote) => {
    if (selectedNote && note.id === selectedNote.id) return;
    let confirm = true;
    if (pendingChanges) {
      confirm = await deleteModal('You have unsaved changes, proceeding will remove them. Continue?');
    }
    if (confirm) {
      setSelectedNote(note);
      setPendingChanges(false);
    }
  };

  const save = (note: INote) => {
    updateNote(note);
    setPendingChanges(false);
  };

  const prevCount = useRef(notes.length);
  useEffect(() => {
    if (notes.length === 0) {
      setSelectedNote(undefined);
      prevCount.current = 0;
    } else if (prevCount.current !== notes.length) {
      setSelectedNote(notes[0]);
      prevCount.current = notes.length;
    }
  }, [notes]);

  return (
    <div style={{ ...bucketFrame, fontFamily: ui.font, background: ui.surface }}>
      <SectionHeader title="Notes" count={notes.length} actionLabel="Add note" onAction={addNote} />
      <LoadingBar loading={loading} height={'3px'} />
      {notes.length === 0 ? (
        <div style={{ padding: '40px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: ui.muted, fontSize: 14 }}>
          No notes on this project yet.
          <button type="button" className="archy-btn" onClick={addNote} style={{ ...button('primary'), height: 38 }}>
            <PlusIcon size={14} />
            Add a note
          </button>
        </div>
      ) : (
        <div className="archy-notes" style={{ display: 'flex', height: 380 }}>
          <div className="archy-notes-list" style={{ flex: '0 0 240px', overflowY: 'auto', background: '#F9FAFB', borderRight: `1px solid ${ui.lineSoft}`, padding: 6, boxSizing: 'border-box' }}>
            {notes.map((note, index) => {
              const on = selectedNote !== undefined && note.id === selectedNote.id;
              return (
                <button
                  key={`${note.id}${index}`}
                  type="button"
                  className={on ? undefined : 'archy-row-btn'}
                  aria-current={on ? 'true' : undefined}
                  onClick={() => selectNote(note)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    textAlign: 'left',
                    padding: '9px 10px 9px 12px',
                    marginBottom: 2,
                    border: 'none',
                    borderLeft: `3px solid ${on ? ui.bucket : 'transparent'}`,
                    borderRadius: 8,
                    background: on ? ui.surface : 'transparent',
                    boxShadow: on ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    fontFamily: 'inherit',
                    cursor: on ? 'default' : 'pointer',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: on ? 600 : 500, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {note.title || 'Untitled note'}
                  </span>
                  <span style={{ flex: 'none', fontSize: 12, color: ui.muted }}>{moment.unix(note.date).format('D MMM YY')}</span>
                </button>
              );
            })}
          </div>
          {selectedNote && (
            <NoteForm note={selectedNote} deleteNote={deleteNote} onSave={save} onChange={() => setPendingChanges(true)} />
          )}
        </div>
      )}
    </div>
  );
};
